// Config checks for event packages (Admin -> Events and Packages).
//
// The same spirit as rateBandValidationUtil: a package can be saved in a
// state that looks fine in the editor but can never actually be booked — an
// option longer than its own window, two rates fighting over the same
// weekday, courts that were deleted since. Those are invisible by eye and
// only show up as a guest being unable to book, so they're surfaced on the
// card while the admin is still looking at it.
//
// ERROR  = this package (or one of its options) cannot be booked as saved.
// WARNING = bookable, but probably not what the admin meant.

import { describeBandDays, bandAppliesOnDay, isHourInBand } from './bookingUtils';
import {
  getPackageWindowHours,
  getPackageCourts,
  getPackageInclusions,
  describePackageWindow,
  getOptionRates,
  getPackageRateBands,
  formatPackageHour,
  describePackageBandHours,
} from './packageUtils';

export const ISSUE_LEVELS = { ERROR: 'error', WARNING: 'warning' };

const err = (code, message, extra = {}) => ({ level: ISSUE_LEVELS.ERROR, code, message, ...extra });
const warn = (code, message, extra = {}) => ({ level: ISSUE_LEVELS.WARNING, code, message, ...extra });

// Which weekdays a rate covers, as a concrete set — an empty/missing `days`
// list means every day, matching bandAppliesOnDay. Needed as a real set (not
// a predicate) so two rates can be compared for overlap.
const daysCovered = (rate) => {
  const days = Array.isArray(rate?.days) ? rate.days.map(Number).filter(d => d >= 0 && d <= 6) : [];
  return days.length === 0 ? [0, 1, 2, 3, 4, 5, 6] : [...new Set(days)];
};

/** "the weekend rate" / "the rate" — how a rate is named inside an error. */
const rateLabel = (rate, rateCount) => {
  if (rateCount < 2) return 'rate';
  const days = describeBandDays(rate);
  return days === 'Every day' ? 'every-day rate' : `${days} rate`;
};

/**
 * Every clock hour a package's window spans, folded to 0-23.
 * A window running 3PM->midnight yields [15..23]; one running 8PM->4AM wraps.
 */
const windowClockHours = (pkg) => {
  const start = Number(pkg?.startHour) || 0;
  return Array.from({ length: getPackageWindowHours(pkg) }, (_, i) => (start + i) % 24);
};

/** "6:00 PM, 7:00 PM" — a short list of hours for a message. */
const describeHourList = (hours) => hours.map(h => formatPackageHour(h)).join(', ');

/**
 * Problems with a package's HOURLY rate bands — the pricing mode used when a
 * package sells no preset durations.
 *
 * Unlike a sport's bands, which fall back to the cheapest rate when nothing
 * covers an hour, a package with an uncovered hour REFUSES to price a booking
 * spanning it (see getPackagePrice). So a gap isn't a mis-charge, it's an
 * hour of the advertised window nobody can book across — worth saying plainly.
 *
 * @param {object} pkg
 * @returns {Array}
 */
const validateRateBands = (pkg) => {
  const issues = [];
  const bands = getPackageRateBands(pkg);
  if (bands.length === 0) return issues;

  bands.forEach((band) => {
    const label = `The ${describePackageBandHours(band)} rate`;
    const price = band?.price;
    if (price === '' || price === null || price === undefined) {
      issues.push(err('band-price-missing', `${label} has no price per hour set.`, { bandId: band?.id }));
    } else if (Number(price) < 0) {
      issues.push(err('band-price-negative', `${label} has a negative price.`, { bandId: band?.id }));
    }
    if (!Number.isFinite(Number(band?.startHour)) || !Number.isFinite(Number(band?.endHour))) {
      issues.push(err('band-hours-missing', `${label} has an incomplete time range.`, { bandId: band?.id }));
    }
  });

  // Two bands over the same hour on the same day: the first listed silently
  // wins (getPackageHourlyRate takes the first match), so the other is dead.
  const clashes = new Map();
  const hours = windowClockHours(pkg);
  for (let day = 0; day < 7; day += 1) {
    hours.forEach((hour) => {
      const covering = bands.filter(b => bandAppliesOnDay(b, day) && isHourInBand(b, hour));
      for (let i = 1; i < covering.length; i += 1) {
        const key = `${covering[0].id}|${covering[i].id}`;
        if (!clashes.has(key)) clashes.set(key, { winner: covering[0], loser: covering[i], days: new Set() });
        clashes.get(key).days.add(day);
      }
    });
  }
  clashes.forEach(({ winner, loser, days }) => {
    issues.push(err(
      'band-overlap',
      `The ${describePackageBandHours(winner)} and ${describePackageBandHours(loser)} rates overlap on `
      + `${describeBandDays({ days: [...days] })}. The first one listed always wins, so the other never applies — `
      + 'split the hours between them or remove one.',
      { bandId: loser?.id },
    ));
  });

  // Hours of the package's own window that no band prices. A day with none at
  // all can't be booked; a partial gap only blocks bookings that span it.
  const gaps = new Map();
  for (let day = 0; day < 7; day += 1) {
    const uncovered = hours.filter(hour => !bands.some(b => bandAppliesOnDay(b, day) && isHourInBand(b, hour)));
    if (uncovered.length === 0) continue;
    const key = uncovered.join(',');
    if (!gaps.has(key)) gaps.set(key, { uncovered, days: [], whole: uncovered.length === hours.length });
    gaps.get(key).days.push(day);
  }
  gaps.forEach(({ uncovered, days, whole }) => {
    const when = describeBandDays({ days });
    issues.push(whole
      ? err('band-day-unpriced', `No hourly rate applies on ${when}, so this package can't be booked at all those days.`)
      : warn(
        'band-gap',
        `No hourly rate covers ${describeHourList(uncovered)} on ${when} — guests won't be able to book across `
        + 'those hours until a rate covers them.',
      ));
  });

  return issues;
};

/**
 * Problems with one package.
 *
 * @param {object} pkg
 * @param {object} config - for cross-checking courts still exist
 * @returns {Array<{level: string, code: string, message: string, optionId?: string}>}
 */
export const validatePackage = (pkg, config) => {
  const issues = [];
  if (!pkg) return issues;

  if (!(pkg.name || '').trim()) {
    issues.push(warn('no-name', 'This package has no name — guests will see it as "Untitled package".'));
  }

  const windowHours = getPackageWindowHours(pkg);
  const options = Array.isArray(pkg.options) ? pkg.options : [];
  const bands = getPackageRateBands(pkg);

  // A package is priced one of two ways and needs one of them: preset
  // durations, or hourly rates the guest books any run against.
  if (options.length === 0 && bands.length === 0) {
    issues.push(err(
      'no-pricing',
      'No pricing set up yet — add a package option (a fixed duration and price), or hourly rates for guests '
      + 'to book any start and end time against. Without one, this package cannot be booked.',
    ));
  } else if (options.length > 0 && bands.length > 0) {
    // Presets win wherever both exist (see getSelectablePackageDurations), so
    // the bands would silently never be offered.
    issues.push(warn(
      'options-and-bands',
      'This package has both preset options and hourly rates. Guests are only ever offered the preset durations, '
      + 'so the hourly rates below are never used — remove whichever you did not mean to keep.',
    ));
  }

  issues.push(...validateRateBands(pkg));

  options.forEach((option) => {
    const hours = Number(option?.hours) || 0;
    const label = hours > 0 ? `The ${hours}-hour option` : 'One option';

    if (hours <= 0) {
      issues.push(err('option-no-hours', 'An option has no duration set — enter how many hours it covers.', { optionId: option?.id }));
    } else if (hours > windowHours) {
      // The headline config error: a 10-hour package inside a 3PM-12AM
      // window can never start anywhere. Name both numbers, since which one
      // is wrong is the admin's call.
      issues.push(err(
        'option-longer-than-window',
        `${label} is longer than the available time range — ${hours} hrs won't fit in ${describePackageWindow(pkg)}. `
        + 'Widen the time range or shorten the option.',
        { optionId: option?.id },
      ));
    } else if (hours === windowHours) {
      // Bookable, but only ever at exactly one start time. Usually means the
      // admin meant the window to be wider.
      issues.push(warn(
        'option-fills-window',
        `${label} exactly fills the time range, so guests can only ever start it at ${describePackageWindow(pkg).split(' – ')[0]}.`,
        { optionId: option?.id },
      ));
    }

    // Each option carries its own list of rates (weekday price, weekend
    // price, …). No rates at all means the duration exists but can never be
    // quoted.
    const rates = getOptionRates(option);
    if (rates.length === 0) {
      issues.push(err(
        'option-no-rates',
        `${label} has no rates — add at least one price, or guests cannot book this duration.`,
        { optionId: option?.id },
      ));
    }

    rates.forEach((rate) => {
      const price = rate?.price;
      const which = rateLabel(rate, rates.length);
      if (price === '' || price === null || price === undefined) {
        issues.push(err('option-price-missing', `${label}'s ${which} has no price set.`, { optionId: option?.id, rateId: rate?.id }));
      } else if (Number(price) < 0) {
        issues.push(err('option-price-negative', `${label}'s ${which} is negative.`, { optionId: option?.id, rateId: rate?.id }));
      }
    });

    // Two rates on the SAME option covering the same weekday means two prices
    // for one thing, and whichever sits earlier silently wins (see
    // getOptionRateForDay). Compared per weekday so a deliberate
    // weekday/weekend split doesn't trip it.
    const seenDays = {};
    const clashingDays = new Set();
    rates.forEach((rate) => {
      daysCovered(rate).forEach((day) => {
        if (seenDays[day]) clashingDays.add(day);
        seenDays[day] = true;
      });
    });
    if (clashingDays.size > 0) {
      issues.push(err(
        'rate-day-overlap',
        `${label} has two rates that both apply on ${describeBandDays({ days: [...clashingDays] })} — `
        + 'guests would be charged whichever is listed first. Split the days between them or remove one.',
        { optionId: option?.id },
      ));
    }

    // Days with no rate at all: the duration simply disappears from the
    // booking form that weekday, which is legitimate (a weekends-only
    // package) but is usually an oversight worth naming.
    const uncovered = [0, 1, 2, 3, 4, 5, 6].filter(day => !seenDays[day]);
    if (rates.length > 0 && uncovered.length > 0) {
      issues.push(warn(
        'rate-days-uncovered',
        `${label} has no rate for ${describeBandDays({ days: uncovered })} — guests won't be offered it on those days.`,
        { optionId: option?.id },
      ));
    }
  });

  // One option per duration is the whole point of nesting rates: a second
  // 4-hour option can only ever fight with the first for the same guests.
  const byHours = {};
  options.forEach((option) => {
    const hours = Number(option?.hours) || 0;
    if (hours <= 0) return;
    (byHours[hours] = byHours[hours] || []).push(option);
  });
  Object.entries(byHours).forEach(([hours, sameDuration]) => {
    if (sameDuration.length < 2) return;
    issues.push(err(
      'duplicate-duration',
      `There are ${sameDuration.length} separate ${hours}-hour options. Combine them into one option `
      + 'with a rate per day range, or guests will be charged whichever is listed first.',
    ));
  });

  const courts = getPackageCourts(pkg);
  if (courts.length === 0) {
    issues.push(err('no-courts', 'No courts assigned — pick which courts this package takes over.'));
  } else {
    // A court deleted from Sports & Courts leaves a dangling number here,
    // which would silently shrink the package rather than fail loudly.
    const known = (config?.courts || []).map(c => Number(c.number));
    const missing = courts.filter(c => !known.includes(c));
    if (missing.length > 0) {
      issues.push(err(
        'court-missing',
        `Court ${missing.join(', ')} ${missing.length > 1 ? 'are' : 'is'} assigned to this package but no longer exists in Sports & Courts.`,
      ));
    }
  }

  const buffer = pkg.bufferMinutes;
  if (buffer !== null && buffer !== undefined && buffer !== '' && Number(buffer) < 0) {
    issues.push(err('buffer-negative', 'The cleanup allowance cannot be negative.'));
  }

  if (getPackageInclusions(pkg).length === 0) {
    issues.push(warn('no-inclusions', "No inclusions listed — guests won't see what the package comes with."));
  }

  return issues;
};

/**
 * Every package's issues, keyed by package id, for a screen-level summary.
 * @returns {{ [packageId: string]: Array }}
 */
export const validatePackages = (config) => {
  const out = {};
  (Array.isArray(config?.packages) ? config.packages : []).forEach((pkg) => {
    const issues = validatePackage(pkg, config);
    if (issues.length > 0) out[pkg.id] = issues;
  });
  return out;
};

/** True when any package has a blocking (ERROR-level) problem. */
export const hasBlockingPackageIssues = (config) =>
  Object.values(validatePackages(config)).some(list => list.some(i => i.level === ISSUE_LEVELS.ERROR));
